/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useReducer } from 'react';
import { View, StyleSheet } from 'react-native';

import { showError, showSuccess } from '../App/AppAlert';

import { DEFAULT_SETTINGS } from '../../support/shared/constants/Constants';
import ensureObject from '../../support/shared/ensureObject';
import { postJSON } from '../../support/Fetch';
import AccountActions from '../../support/AccountActions';
import connectData, { ConnectedProps } from '../../support/connectData';

import Editor, {
  EditorContent,
  createEditorContent,
} from '../../core-ui/Editor/StatefulEditor';
import LoadingIndicator from '../uiold/LoadingIndicator';
import { Card, Page, Button, ButtonToolbar, Text, TextInput } from '../uiold';
import { canUser } from '../../support/shared/permissionHelpers';

import { Account } from '../../types/shared/Account';
import { User } from '../../types/shared/User';

type EmailSettings = {
  subject: string;
  emailContent: EditorContent;
};

type EmailSettingsWrapperProps = {
  title: string;
  titleVerb: string;
  settingsKey: EmailTypes;
};

type Props = {
  account: Account;
  user: User | null | undefined;
  title: string;
  settingsKey: EmailTypes;
  titleVerb: string;
};

type State = {
  account: Account;
  subject: string;
  emailContent: EditorContent;
  isSaving: boolean;
  emailKey: EmailTypes;
  resetCount: number;
};
type Action =
  | { type: 'SUBJECT_CHANGE'; subject: string }
  | { type: 'CONTENT_CHANGE'; content: EditorContent }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_COMPLETE' }
  | { type: 'RESET_FORM' };

function contentReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SUBJECT_CHANGE': {
      let { subject } = action;
      return { ...state, subject };
    }
    case 'CONTENT_CHANGE': {
      let { content } = action;
      return { ...state, emailContent: content };
    }
    case 'SAVE_START': {
      return { ...state, isSaving: true };
    }
    case 'SAVE_COMPLETE': {
      return { ...state, isSaving: false };
    }
    case 'RESET_FORM': {
      let { subject, emailContent } = getEmail(state.account, state.emailKey);
      return {
        ...state,
        subject,
        emailContent,
        // This is incremented to trigger unmount/remount on stateful
        // components.
        resetCount: state.resetCount + 1,
      };
    }
    default: {
      return state;
    }
  }
}

function saveChanges(params: {
  state: State;
  dispatch: (action: Action) => void;
  key: EmailTypes;
}) {
  let { state, dispatch, key } = params;
  let { account, subject, emailContent } = state;
  let email = { subject, emailContent: emailContent.toString() };
  let updates = { [key]: email };
  let accountID = account.id;
  let postData = { account: { settings: updates } };
  postJSON(`/api/accounts/${accountID}/settings`, postData, (error, data) => {
    if (error) {
      let errorObject = ensureObject(error);
      let errorMessage = errorObject
        ? String(errorObject.message)
        : String(error);
      showError(`Unable to complete request: ${errorMessage}`);
      return;
    }
    if (data.success) {
      AccountActions.updateSettings(updates);
      showSuccess(`Saved Successfully.`);
    }
    dispatch({ type: 'SAVE_COMPLETE' });
  });
  dispatch({ type: 'SAVE_START' });
}

function EmailSettings(props: Props) {
  let { account, title, settingsKey, titleVerb, user } = props;
  let [state, dispatch] = useReducer(
    contentReducer,
    getInitialState(account, settingsKey),
  );
  let { isSaving, subject, emailContent, resetCount } = state;
  let userCanWrite = user && canUser(user, { settings: 'WRITE' });
  let renderSaveControls = () => {
    let { subject: oldSubject, emailContent: oldEmailContent } = getEmail(
      account,
      settingsKey,
    );
    let hasSubjectChanged = oldSubject !== subject;
    // TODO: Compare these content objects without converting to string, or else
    // track hasContentChanged independently via state.
    let hasContentChanged =
      emailContent.toString() !== oldEmailContent.toString();
    if ((!hasSubjectChanged && !hasContentChanged) || !userCanWrite) {
      return null;
    }
    return isSaving ? (
      <LoadingIndicator />
    ) : (
      <ButtonToolbar>
        <Button
          title="Revert"
          onPress={() => dispatch({ type: 'RESET_FORM' })}
        />
        <Button
          title="Save"
          onPress={() => saveChanges({ state, dispatch, key: settingsKey })}
          isPrimary
        />
      </ButtonToolbar>
    );
  };
  return (
    <Page>
      <Page.Header title={title}>{renderSaveControls()}</Page.Header>
      <Card style={styles.marginBottom}>
        <Text style={styles.marginBottom}>
          If you fill in the following fields, an email will be sent out
          whenever you {titleVerb.toLowerCase()} a vendor.
        </Text>
        <Text style={styles.marginBottom}>Subject</Text>
        <TextInput
          disabled={!userCanWrite}
          onChangeText={(text) => {
            dispatch({ type: 'SUBJECT_CHANGE', subject: text });
          }}
          value={subject}
        />
        <View>
          <Text style={styles.marginVertical}>Content</Text>
          <Editor
            key={resetCount}
            height={280}
            initialContent={emailContent}
            onChange={(content) =>
              dispatch({ type: 'CONTENT_CHANGE', content })
            }
            readOnly={!userCanWrite}
          />
        </View>
      </Card>
    </Page>
  );
}

type EmailTypes =
  | 'approval_email'
  | 'declined_email'
  | 'waitlisted_email'
  | 'invoice_email';

// To add a new type of email you can see how approval email is handled in routes.ts
// We just need to define component with route and props

function EmailSettingsWrapper(props: EmailSettingsWrapperFullProps) {
  let { account, title, settingsKey, titleVerb, user } = props;
  return account ? (
    <EmailSettings
      user={user}
      account={account}
      title={title}
      settingsKey={settingsKey}
      titleVerb={titleVerb}
    />
  ) : null;
}

function getInitialState(account: Account, emailKey: EmailTypes): State {
  let { subject, emailContent } = getEmail(account, emailKey);
  return {
    account,
    subject,
    emailContent,
    isSaving: false,
    emailKey,
    resetCount: 0,
  };
}

function getEmail(account: Account, emailKey: EmailTypes): EmailSettings {
  let emailKeySettings = account?.settings?.[emailKey];
  if (emailKeySettings) {
    let { subject, emailContent } = emailKeySettings;
    return {
      subject,
      emailContent: createEditorContent(emailContent),
    };
  } else {
    let { subject, emailContent } = DEFAULT_SETTINGS[emailKey];
    return {
      subject,
      emailContent: createEditorContent(emailContent),
    };
  }
}

let styles = StyleSheet.create({
  marginBottom: {
    marginBottom: 10,
  },
  marginVertical: {
    marginVertical: 10,
  },
});

const connector = connectData<EmailSettingsWrapperProps>()(() => ({
  account: { dataStoreKey: 'account' },
  user: { dataStoreKey: 'user' },
}));
type EmailSettingsWrapperFullProps = ConnectedProps<typeof connector>;
export default connector(EmailSettingsWrapper);
